import type { SessionSnapshot } from "@hatch/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { frameSandbox, frameUrl, isRenderableSiteUrl } from "../../lib/frameUrl.js";
import { describeElapsed } from "../../lib/labels.js";
import { usePreviewFrame } from "../../lib/usePreviewFrame.js";
import type { Step } from "../../lib/viewModel.js";
import { HANDOFF_COPY, backendHandoffPath } from "../../lib/xanoHandoff.js";
import { HatchScreen, Header } from "./Scenery.js";
import { ShellLog } from "./ShellLog.js";
import { StagePills } from "./StagePills.js";
import type { Pill } from "./stage.js";

/**
 * The reveal.
 *
 * Reached only once the site has actually served to this browser — the gate is
 * in `BuildView`, because it decides which screen shows rather than what this
 * one contains. By the time anything here renders, the frame below is pointing
 * at an address that answered a real request moments ago.
 *
 * It keeps the building screen's frame rather than replacing it: same header,
 * same stage tracker, same log in the same column. Only the left pane changes —
 * the egg and the mountain give way to the site that came out of them. The
 * build the user just sat through is still readable beside their site, which is
 * where the questions they have at this point ("what did it actually write?")
 * get answered, and the screen resolves into its result instead of cutting to a
 * different place.
 *
 * That cost this screen the celebration it used to be: a full-page reveal with
 * shell fragments flying off the headline and the parent squawking at the site
 * from a corner vignette. The headline stayed and the rest went, because none of
 * it fits beside a working pane and none of it was worth the log.
 */
/**
 * What the build registered, as the contract carries it.
 *
 * Taken from `SessionSnapshot` rather than restated, so a field added there
 * cannot quietly mean something else here.
 */
type BackendObjects = NonNullable<NonNullable<SessionSnapshot["xanoBackend"]>["objects"]>;

export function DoneView({
  siteUrl,
  prompt,
  buildMs,
  terminalAt,
  pills,
  steps,
  fileCount,
  sessionId,
  hasBackend,
  objects,
  sitePages,
  onRestart,
}: {
  siteUrl: string;
  prompt: string | null;
  buildMs: number | null;
  terminalAt: number | null;
  pills: Pill[];
  steps: Step[];
  fileCount: number;
  sessionId: string;
  /** Whether this build has a Xano backend worth offering. See lib/xanoHandoff. */
  hasBackend: boolean;
  /**
   * What the build registered, when Hatch could establish it. Every field is
   * independently optional and an absent one renders as no pill at all — see
   * `SessionSnapshot`, where the absent-means-unknown rule is set out.
   */
  objects?: BackendObjects | null;
  /** Documents the deploy published, when it reported a count. */
  sitePages?: number | null;
  onRestart: () => void;
}) {
  return (
    <HatchScreen fill>
      <Header prompt={prompt}>
        <span className="shrink-0 text-xs font-extrabold tracking-[0.1em] uppercase text-moss">
          &#9679; Live
        </span>
      </Header>

      <StagePills pills={pills} />

      {/*
        `min-h-0` on the row and on each pane is load-bearing. A flex child
        defaults to a content-derived minimum size and refuses to shrink below
        it, which makes the log's `overflow-y-auto` do nothing at all and turns
        the frame back into an unbounded page.

        Stacked below the breakpoint the panes do not split the height, they take
        turns: the preview owns the viewport and the log collapses. A phone has
        room for one pane, not two — half each leaves the log too short to read
        as a feed and the site too short to read as a site — and of the two, the
        site is what the user came for. Same trade the classic build view makes
        once there is something to preview.
      */}
      <main className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {/* `z-[2]` on both panes puts them in front of the cloud sea, which is
            fixed to the bottom 70px of every screen in this skin and would
            otherwise be painted across the foot of the preview and the last two
            rows of the log. */}
        <section className="relative z-[2] flex min-h-0 flex-1 basis-full flex-col p-4 lg:basis-0 lg:grow-[5] lg:py-6 lg:pr-5 lg:pl-6">
          <SitePreview
            siteUrl={siteUrl}
            buildMs={buildMs}
            terminalAt={terminalAt}
            sessionId={sessionId}
            hasBackend={hasBackend}
            objects={objects ?? null}
            sitePages={sitePages ?? null}
            onRestart={onRestart}
          />
        </section>

        {/* Gone below the breakpoint rather than collapsed to nothing: a pane
            that is merely invisible still holds focusable controls, and a
            keyboard or screen-reader user on a phone would tab into a log they
            cannot see. */}
        <div className="relative z-[2] hidden min-h-0 basis-0 flex-col overflow-hidden lg:flex lg:grow-[4] lg:py-6 lg:pr-6 lg:pl-0">
          {/* The build is over by construction: the canvas only reaches
              `framing` on a terminal event carrying a site URL. */}
          <ShellLog steps={steps} isTerminal fileCount={fileCount} />
        </div>
      </main>
    </HatchScreen>
  );
}

/**
 * The left pane: the site, and the four things to do with it.
 *
 * The address is the deliverable and the frame is the enhancement — a
 * cross-origin frame that rendered nothing looks exactly like one that
 * rendered, so the link sits above it and the way out sits below it in every
 * state.
 *
 * It no longer carries `HANDOFF_COPY.hosted` ("Really hosted, really online").
 * The claim is true and it is still worth making, but the header had grown to
 * four stacked lines above the frame — headline, eyebrow, address, claim — and
 * the one paying for it was the site, which is the only thing here nobody needs
 * a sentence to believe. What remains says it anyway: an address the user can
 * click, a copy button, and a footer that tells them to open it in a new tab.
 * The classic skin still shows the line, which is why it stays in lib.
 */
function SitePreview({
  siteUrl,
  buildMs,
  terminalAt,
  sessionId,
  hasBackend,
  objects,
  sitePages,
  onRestart,
}: {
  siteUrl: string;
  buildMs: number | null;
  terminalAt: number | null;
  sessionId: string;
  hasBackend: boolean;
  objects: BackendObjects | null;
  sitePages: number | null;
  onRestart: () => void;
}) {
  // Held so a second click restarts the window instead of inheriting the first
  // click's deadline — press twice inside 1.6s and the label used to snap back
  // partway through the second press, which reads as the copy having failed.
  // Cleared on unmount so the timer is not still holding a setter for a tree
  // that went away when "Hatch another!" was pressed.
  const copiedTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [copied, setCopied] = useState(false);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const { loadKey, reloading, reload } = usePreviewFrame(siteUrl);
  const remaining = useNestWarmth(terminalAt);
  const followable = isRenderableSiteUrl(siteUrl);

  async function copy() {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard access can be denied; the URL is selectable either way.
    }
  }

  return (
    /*
      One card, the way the log beside it is one card.

      The pieces used to sit loose on the sky with only the frame boxed, and the
      sky in this skin is not a flat colour — clouds drift behind it and the sun
      sits in the top right. Cream text on cream paper with a white cloud
      passing under it is where the address stopped being readable. An opaque
      surface is the fix, and it is the surface this skin already uses for
      anything that is information rather than scenery.

      It also reads as a browser window now, which is what it is: chrome across
      the top with the address and the controls, the page below it, a footnote
      underneath. That was a side effect rather than the goal, but it is the
      right shape for the thing.
    */
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border-2 border-shell-rule bg-card shadow-[0_6px_0_rgb(62_50_38_/_0.12)] animate-[popIn_0.4s_ease]">
      {/* The result, stated as a result.

          It used to share a row with the address and three buttons, which gave
          the moment the build lands the same weight as a copy button. The two
          numbers beside it are the two questions a finished build actually
          raises — how long did that take, and how long do I have — and both
          used to live in a sentence under the frame, past the fold of a pane
          built to be filled by someone else's scrolling page. */}
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-3 border-b-2 border-shell-rule bg-shell px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <span
            aria-hidden
            className="grid size-10 shrink-0 place-items-center rounded-[12px] bg-ember text-[20px] text-shell shadow-[0_3px_0_var(--color-ember-shadow)]"
          >
            ✦
          </span>
          <div className="min-w-0">
            <h1 className="font-chewy text-[clamp(22px,2.6vw,28px)] leading-tight font-normal text-ember">
              It hatched!
            </h1>
            <p className="text-[13px] font-semibold text-bark-soft">
              Frontend and backend, both real, both yours.
            </p>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-stretch gap-2">
          <Readout label="Hatched in">
            {buildMs === null ? "—" : describeElapsed(buildMs)}
          </Readout>
          <NestReadout remaining={remaining} />
          <button
            type="button"
            onClick={onRestart}
            className="cursor-pointer self-center rounded-lg bg-ember px-5 py-2 font-chewy text-[16px] tracking-[0.04em] text-shell shadow-[0_4px_0_var(--color-ember-shadow)] transition-transform hover:-translate-y-0.5"
          >
            Hatch another!
          </button>
        </div>
      </div>

      {/* The two halves of what was built, side by side and above the frame.

          The backend half was a thin band here before, and under the frame
          before that — where the user reached the end of their own page before
          they reached the offer, and most never did. Given a card of the same
          size as the site's, the screen stops implying that the site is the
          build and the backend is a footnote. */}
      <div className="grid shrink-0 gap-3 border-b-2 border-shell-rule px-4 py-3 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col rounded-lg border-2 border-shell-rule bg-card p-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-[11px] font-extrabold tracking-[0.16em] uppercase text-moss">
              <span aria-hidden className="text-[9px] leading-none">
                ●
              </span>
              Your site is live
            </h2>
            {followable && (
              <button
                type="button"
                onClick={() => void reload()}
                disabled={reloading}
                title="Reload the preview"
                className="cursor-pointer rounded-md px-1.5 py-0.5 text-[13px] leading-none text-bark-faint transition-colors hover:text-ember disabled:opacity-60"
              >
                <span aria-hidden>↻</span>
                <span className="sr-only">{reloading ? "Reloading" : "Reload"}</span>
              </button>
            )}
          </div>

          {/* The address, in a field with the copy control attached to it.

              An address we cannot follow is still an address worth showing — as
              text. See isRenderableSiteUrl.

              `truncate` rather than wrapping: this is not our string, and a
              generated subdomain on a platform host runs well past 40
              characters. Broken across lines it would push the frame down the
              pane; the copy button beside it is how most people take it anyway,
              and the full address is in the title. */}
          <div className="mt-2 flex items-stretch overflow-hidden rounded-lg border-2 border-shell-rule bg-shell">
            <div className="min-w-0 flex-1 px-2.5 py-1.5">
              {followable ? (
                <a
                  href={siteUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={siteUrl}
                  className="block truncate font-plex text-[12.5px] text-bark transition-colors hover:text-ember"
                >
                  {siteUrl}
                </a>
              ) : (
                <span className="block truncate font-plex text-[12.5px] text-bark-faint" title={siteUrl}>
                  {siteUrl}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={copy}
              className="shrink-0 cursor-pointer border-l-2 border-shell-rule bg-card px-3 font-nunito text-[12.5px] font-extrabold text-bark transition-colors hover:text-ember"
            >
              {copied ? "Copied!" : "Copy link"}
            </button>
          </div>

          {/* Only what the deploy actually reported. A line describing the site
              in numbers Hatch had to guess at would be decoration wearing the
              costume of evidence. */}
          {sitePages !== null && (
            <p className="mt-auto pt-2 text-[11.5px] font-semibold text-bark-faint">
              {countLabel(sitePages, "page", "pages")} deployed
            </p>
          )}
        </section>

        {hasBackend && <BackendCard sessionId={sessionId} objects={objects} />}
      </div>

      {/* The site itself. `min-h-0 flex-1` is what makes the frame take the
          rest of the card rather than a fixed slab of it, so the preview grows
          with the window the way the log beside it does. */}
      <div className="relative min-h-0 flex-1 bg-shell p-2">
        {followable ? (
          <iframe
            src={frameUrl(siteUrl, loadKey)}
            title="Preview of your site"
            // Generated by an agent from a user prompt: treated as untrusted
            // third-party content, and given no access back to Hatch. The grant
            // is computed rather than literal — see `frameSandbox`, which adds
            // `allow-same-origin` only for a site that is provably not us,
            // because without it a page that touches localStorage dies before
            // it mounts and previews as a white rectangle.
            sandbox={frameSandbox(siteUrl)}
            referrerPolicy="no-referrer"
            className="size-full rounded-[6px] border-0 bg-white"
          />
        ) : (
          <div className="flex size-full items-center justify-center rounded-[6px] bg-white px-8 text-center">
            <p className="text-[15px] font-semibold text-bark-soft">
              The builder reported an address Hatch will not open.
            </p>
          </div>
        )}
      </div>

      {/* The way out of the frame, which is the one thing under it that cannot
          move into a card above it. Not conditional on anything: a cross-origin
          frame that rendered nothing looks exactly like one that rendered, so
          the escape hatch cannot depend on knowing which happened. */}
      <p className="shrink-0 border-t-2 border-shell-rule px-4 py-2.5 text-center text-[12.5px] font-bold text-pretty text-bark-soft">
        {followable ? (
          <a href={siteUrl} target="_blank" rel="noreferrer">
            open your site in a new tab
          </a>
        ) : (
          "open your site in a new tab"
        )}
        {remaining === 0 ? " to check whether the nest has cooled." : " for the real thing."}
      </p>
    </div>
  );
}

/**
 * The other half of what was built.
 *
 * The frame below shows the site. Nothing else on this screen shows the backend
 * behind it — the tables, the endpoints, the functions the user watched scroll
 * past in the log — and without this the whole Xano half of the build is
 * something they were told about rather than something they saw.
 *
 * The pills are the difference between a claim and evidence, which is why they
 * are worth the plumbing that produces them (api/src/agent/backend-counts.ts).
 * Every one of them is a number Hatch counted off the source it deployed, and a
 * number it could not establish renders as no pill rather than as a zero.
 *
 * It wears Xano's colours rather than the skin's, deliberately. This is a
 * handoff to a different product, and a handoff painted in ember-on-paper reads
 * as one more Hatch button; the visual break IS the message that there is
 * somewhere else to go. The tokens are Xano's own — see index.css.
 *
 * An ordinary anchor to a Hatch route, not a fetch. What sits behind that route
 * is a one-time token with a 60-second life, so there is no URL to render here
 * and nothing to prefetch usefully — see api/src/xano/impersonate.ts.
 */
function BackendCard({
  sessionId,
  objects,
}: {
  sessionId: string;
  objects: BackendObjects | null;
}) {
  const pills = [
    countLabel(objects?.tables, "table", "tables"),
    countLabel(objects?.apis, "API", "APIs"),
    countLabel(objects?.functions, "function", "functions"),
  ].filter((label): label is string => label !== null);

  return (
    <section className="flex min-w-0 flex-col rounded-lg bg-xano-deep p-3">
      <h2 className="flex items-center gap-2 text-[11px] font-extrabold tracking-[0.16em] uppercase text-white">
        <span
          aria-hidden
          className="grid size-5 shrink-0 place-items-center rounded bg-xano text-[11px] tracking-normal text-white"
        >
          X
        </span>
        {HANDOFF_COPY.heading}
      </h2>

      {pills.length > 0 && (
        <ul className="mt-2.5 flex flex-wrap gap-1.5">
          {pills.map((label) => (
            <li
              key={label}
              className="rounded-md bg-white/10 px-2 py-1 font-plex text-[12px] text-white"
            >
              {label}
            </li>
          ))}
        </ul>
      )}

      {/* Said out loud rather than left to be discovered. The session really is
          read-only, and someone who tries to add a field and cannot must read
          that as the tour it is, not as a product that is broken. */}
      <p className="mt-2.5 text-[11.5px] font-semibold text-xano-tint">{HANDOFF_COPY.scope}</p>

      <a
        href={backendHandoffPath(sessionId)}
        target="_blank"
        rel="noreferrer"
        className="mt-auto block rounded-lg bg-xano px-4 py-2.5 pt-2.5 text-center font-nunito text-[14px] font-extrabold text-white transition-colors hover:bg-xano-hover"
      >
        {HANDOFF_COPY.action} <span aria-hidden>↗</span>
      </a>
    </section>
  );
}

/**
 * One labelled number in the hero band.
 *
 * Boxed rather than set in a sentence because these are readings, and a reading
 * wants a label above it and nothing else on the line — the sentence they came
 * from ("Hatched in 4m 17s · nest cools in 48m — open your site…") made both
 * numbers scan as prose and neither as a fact.
 */
function Readout({
  label,
  tone = "quiet",
  children,
}: {
  label: string;
  tone?: "quiet" | "warm";
  children: ReactNode;
}) {
  const warm = tone === "warm";
  return (
    <div
      className={`min-w-[104px] rounded-lg border-2 px-3 py-1.5 ${
        warm ? "border-mountain bg-sun/25" : "border-shell-rule bg-card"
      }`}
    >
      <p
        className={`text-[9.5px] font-extrabold tracking-[0.14em] uppercase ${
          warm ? "text-nest" : "text-bark-faint"
        }`}
      >
        {label}
      </p>
      <p
        className={`font-nunito text-[17px] leading-tight font-extrabold ${
          warm ? "text-nest-deep" : "text-bark"
        }`}
      >
        {children}
      </p>
    </div>
  );
}

/**
 * How long the site has left, as a reading and a bar.
 *
 * The bar is drawn against `NEST_WARMTH_MS` and nothing else, because that is
 * all this screen knows: the countdown is an estimate from the terminal event
 * against a constant, not a value the API reports (see `useNestWarmth`). The
 * bar makes the same claim the prose already made — "about an hour" — rather
 * than a more precise one, and it disappears entirely when there is no terminal
 * timestamp to anchor to, since a bar with nothing behind it is the one part of
 * this that would be inventing precision.
 */
function NestReadout({ remaining }: { remaining: number | null }) {
  if (remaining === null) {
    return <Readout label="Nest cools in">about an hour</Readout>;
  }

  return (
    <Readout label="Nest cools in" tone="warm">
      {remaining === 0 ? "cooled" : describeWarmth(remaining)}
      <span
        aria-hidden
        className="mt-1 block h-1 w-full overflow-hidden rounded-full bg-mountain/30"
      >
        <span
          className="block h-full rounded-full bg-mountain transition-[width] duration-1000 ease-linear"
          style={{ width: `${String(Math.round((remaining / NEST_WARMTH_MS) * 100))}%` }}
        />
      </span>
    </Readout>
  );
}

/**
 * `6 tables`, `1 API`, or nothing at all.
 *
 * `null` in, `null` out — an absent count means Hatch could not establish it,
 * and the pill it would have drawn is the thing that must not appear. Zero is a
 * real answer ("this build has no functions") and also renders as nothing,
 * because a pill reading `0 functions` is a fact nobody came here for.
 */
function countLabel(count: number | null | undefined, one: string, many: string): string | null {
  if (count === null || count === undefined || count <= 0) return null;
  return `${String(count)} ${count === 1 ? one : many}`;
}

/**
 * How long the deployed site has left.
 *
 * Xano's ephemeral environments live about an hour, which is what the landing
 * page's footer already promises. The clock starts at the terminal event — the
 * moment the deploy reported a URL — because that is the closest thing in the
 * stream to when the environment came up.
 *
 * "About" is doing real work in that sentence: this is a client-side estimate
 * against a constant, not a value the API reports. It is honest enough to tell
 * someone to open their site now and wrong enough that it should never be the
 * basis of anything but that nudge. If the session snapshot ever carries a real
 * expiry, this should read it instead.
 */
const NEST_WARMTH_MS = 60 * 60_000;

/** The reading shows seconds now, so it has to move every second to stay true. */
const TICK_MS = 1_000;

/**
 * The countdown's own format: `48m 28s`, and `1h` for the first moment.
 *
 * `describeElapsed` is not reused here: it is built for build durations, and it
 * renders a full hour as "60m". Seconds are shown because the readout is a
 * clock now rather than a clause in a sentence — a number that sits still for
 * thirty seconds beside a progress bar reads as a stalled screen.
 */
function describeWarmth(ms: number): string {
  const total = Math.ceil(ms / 1000);
  if (total >= 3600) return "1h";
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return minutes === 0 ? `${String(seconds)}s` : `${String(minutes)}m ${String(seconds)}s`;
}

function useNestWarmth(terminalAt: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (terminalAt === null) return;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [terminalAt]);

  if (terminalAt === null) return null;
  return Math.max(0, terminalAt + NEST_WARMTH_MS - now);
}

export const NEST_WARMTH_MS_FOR_TEST = NEST_WARMTH_MS;
